import {
  AsYouType,
  getCountries,
  getCountryCallingCode,
  parsePhoneNumberFromString,
  type CountryCode,
} from "libphonenumber-js/min"

export type { CountryCode }

export const TOP_COUNTRIES: CountryCode[] = [
  "BR",
  "US",
  "PT",
  "AR",
  "UY",
  "PY",
  "CL",
  "GB",
  "DE",
  "FR",
  "IT",
]

const DEFAULT_COUNTRY_BY_LOCALE: Record<string, CountryCode> = {
  "pt-BR": "BR",
  "en-US": "US",
  "es": "AR",
}

export function getDefaultCountry(locale: string | null | undefined): CountryCode {
  if (!locale) return "BR"
  return DEFAULT_COUNTRY_BY_LOCALE[locale] ?? "BR"
}

export function getDialCode(country: CountryCode): string {
  return `+${getCountryCallingCode(country)}`
}

export function flagEmoji(country: CountryCode): string {
  return country
    .toUpperCase()
    .replace(/./g, (c) => String.fromCodePoint(0x1f1a5 + c.charCodeAt(0)))
}

export function listAllCountries(): CountryCode[] {
  return getCountries()
}

export function formatAsYouType(input: string, country: CountryCode): string {
  if (!input) return ""
  return new AsYouType(country).input(input)
}

export function isValidPhone(raw: string | null | undefined): boolean {
  if (!raw) return false
  const parsed = parsePhoneNumberFromString(raw)
  return Boolean(parsed?.isValid())
}

export function normalizePhoneE164(
  raw: string | null | undefined,
): string | null {
  if (!raw) return null
  const trimmed = raw.trim()
  if (!trimmed) return null
  const parsed = parsePhoneNumberFromString(trimmed)
  if (parsed?.isValid()) return parsed.number
  return trimmed
}

export function formatPhoneForDisplay(
  raw: string | null | undefined,
): string {
  if (!raw) return ""
  const parsed = parsePhoneNumberFromString(raw)
  if (parsed?.isValid()) return parsed.formatInternational()
  return raw
}

export function waMeUrl(raw: string | null | undefined): string | null {
  if (!raw) return null
  const parsed = parsePhoneNumberFromString(raw)
  if (parsed?.isValid()) {
    return `https://wa.me/${parsed.number.slice(1)}`
  }
  const digits = raw.replace(/\D/g, "")
  return digits ? `https://wa.me/${digits}` : null
}

export function parsePhone(
  raw: string | null | undefined,
  defaultCountry?: CountryCode,
): { country: CountryCode; national: string; isValid: boolean } | null {
  if (!raw) return null
  const parsed = parsePhoneNumberFromString(raw, defaultCountry)
  if (!parsed) return null
  const country = parsed.country
  if (!country) return null
  return {
    country,
    national: parsed.formatNational(),
    isValid: parsed.isValid(),
  }
}
